import { nanoid } from 'nanoid';

function field(name, type, required = false, description = '') {
  return { id: nanoid(), name, type, required, description };
}
function idField() {
  return { id: nanoid(), name: 'id', type: 'auto-id', required: true, isDocId: true, description: 'Auto-generated document ID' };
}

function node(id, type, x, y, label, fields, subcollections = [], description = '') {
  return { id, type, position: { x, y }, data: { label, fields, subcollections, description } };
}

function edge(source, target, relationshipType, label = '') {
  return { id: nanoid(), source, target, type: 'relationship', data: { relationshipType, label, description: '' } };
}

const N = {
  mobileApp:           'n-mobile-app',
  stripe:              'n-stripe',
  activities:          'n-activities',
  batches:             'n-batches',
  devices:             'n-devices',
  devLogs:             'n-dev-logs',
  devMonitoring:       'n-dev-monitoring',
  devQueue:            'n-dev-queue',
  gateCrossings:       'n-gate-crossings',
  gateCrossingsRetool: 'n-gate-crossings-retool',
  gates:               'n-gates',
  locations:           'n-locations',
  media:               'n-media',
  opsConfig:           'n-ops-config',
  products:            'n-products',
  stripeEvents:        'n-stripe-events',
  userMediaInactive:   'n-user-media-inactive',
  usernames:           'n-usernames',
};

const schemaNodes = [
  node(N.mobileApp, 'external', -320, 140, 'Mobile App',
    [field('userId', 'string', true, 'Firebase Auth UID')], [], 'iOS/Android client app used by riders'),
  node(N.stripe, 'external', -320, 820, 'Stripe', [], [], 'Payment processor'),

  node(N.activities, 'collection', 100, 140, 'activities',
    [idField(), field('userId', 'reference', true, '→ users (Firebase Auth UID)'),
     field('startedAt', 'timestamp', true), field('endedAt', 'timestamp'), field('createdAt', 'timestamp')],
    ['batches'], 'A single user activity session (e.g. a ride)'),

  node(N.batches, 'subcollection', 100, 500, 'activities / batches',
    [idField(), field('createdAt', 'timestamp', true),
     field('data', 'array', true, 'Array of GPS points: {accuracy, altitude, course, latitude, longitude, speed, timestamp}')],
    [], 'Batched GPS data uploads during an activity'),

  node(N.gateCrossings, 'collection', 550, 140, 'gate_crossings',
    [idField(), field('activity_id', 'reference', true, '→ activities'),
     field('gate_id', 'reference', true, '→ gates'), field('user_id', 'reference', true, '→ users (Firebase Auth UID)'),
     field('timestamp', 'timestamp', true), field('intersection_lat', 'number'), field('intersection_lon', 'number'),
     field('movement_cross_product', 'number', false, 'Used to determine crossing direction')],
    [], 'Recorded crossing of a gate by a user during an activity'),

  node(N.gateCrossingsRetool, 'collection', 550, 700, 'gate_crossings_retool',
    [idField()], [], 'Mirror/operational copy of gate_crossings for Retool dashboards'),

  node(N.gates, 'collection', 1000, 60, 'gates',
    [idField(), field('location_id', 'reference', true, '→ locations'),
     field('location_name', 'string', false, 'Denormalized location name'),
     field('placement', 'string', false, 'e.g. "MTB Big Airbag"'),
     field('coordinates', 'array', true, 'Array of {latitude, longitude} polygon points'),
     field('trigger_polarity', 'boolean', true, 'Direction of gate crossing that counts'),
     field('userId', 'reference', false, 'User who created the gate'), field('createdAt', 'timestamp')],
    [], 'A virtual gate/geofence at a location'),

  node(N.locations, 'collection', 1450, 60, 'locations',
    [idField(), field('city', 'string'), field('address', 'map', false, '{city, country, state, street, zip}'),
     field('coordinates', 'map', false, '{lat, lon, zoom}'), field('coverImage', 'url'),
     field('hidden', 'boolean'), field('images', 'array')],
    [], 'A physical location (bike park, trail system, etc.)'),

  node(N.devices, 'collection', 1000, 400, 'devices',
    [idField(), field('capturePoint_id', 'string', true, 'Human-readable ID, e.g. "CP82"'),
     field('config', 'map', false, 'Audio/video capture config')],
    ['logs', 'monitoring', 'queue'], 'Physical capture point devices (cameras) installed at gates'),

  node(N.devLogs, 'subcollection', 1450, 380, 'devices / logs',
    [idField(), field('createdAt', 'timestamp'), field('message', 'string'), field('level', 'string')],
    [], 'Device log entries'),

  node(N.devMonitoring, 'subcollection', 1450, 560, 'devices / monitoring',
    [idField(), field('timestamp', 'timestamp'), field('status', 'map')],
    [], 'Device health/status snapshots'),

  node(N.devQueue, 'subcollection', 1450, 740, 'devices / queue',
    [idField(), field('createdAt', 'timestamp'), field('payload', 'map')],
    [], 'Command queue for the device'),

  node(N.media, 'collection', 550, 420, 'media',
    [idField(), field('activity_id', 'reference', true, '→ activities (or "Ops Trigger" for manual captures)'),
     field('capturePoint_id', 'reference', true, '→ devices'),
     field('concatenated_video', 'boolean'), field('config_snapshot', 'map', false, 'Copy of device config at time of capture')],
    [], 'Captured media files (video clips) from a capture point'),

  node(N.opsConfig, 'collection', 100, 920, 'ops_config', [idField()], [], 'Operational configuration documents'),

  node(N.products, 'collection', 550, 920, 'products',
    [idField(), field('name', 'string'), field('stripeProductId', 'string'), field('price', 'number')],
    [], 'Purchasable products (linked to Stripe)'),

  node(N.stripeEvents, 'collection', 100, 700, 'stripe_events',
    [idField(), field('type', 'string', true, 'Stripe event type, e.g. "checkout.session.completed"'),
     field('data', 'map'), field('createdAt', 'timestamp')],
    [], 'Raw Stripe webhook events'),

  node(N.userMediaInactive, 'collection', 1000, 920, 'user_media_inactive',
    [idField()], [], 'Inactive/archived user media records'),

  node(N.usernames, 'collection', 1450, 920, 'usernames',
    [idField(), field('userId', 'reference', true, 'Firebase Auth UID')],
    [], 'Username → userId lookup table (doc ID is the username)'),
];

const schemaEdges = [
  edge(N.mobileApp, N.activities, 'writes', 'creates'),
  edge(N.activities, N.batches, 'subcollection'),
  edge(N.activities, N.gateCrossings, 'reference', 'activity_id'),
  edge(N.gates, N.gateCrossings, 'reference', 'gate_id'),
  edge(N.activities, N.media, 'reference', 'activity_id'),
  edge(N.devices, N.media, 'reference', 'capturePoint_id'),
  edge(N.locations, N.gates, 'reference', 'location_id'),
  edge(N.gateCrossings, N.gateCrossingsRetool, 'mirrors'),
  edge(N.devices, N.devLogs, 'subcollection'),
  edge(N.devices, N.devMonitoring, 'subcollection'),
  edge(N.devices, N.devQueue, 'subcollection'),
  edge(N.stripe, N.stripeEvents, 'writes'),
  edge(N.stripe, N.products, 'writes', 'syncs'),
];

const schemaPage = {
  id: 'page-schema',
  name: 'Schema Overview',
  nodes: schemaNodes,
  edges: schemaEdges,
  viewport: { x: 380, y: 80, zoom: 0.75 },
};

const emptyPage = {
  id: 'page-flows',
  name: 'Data Flows',
  nodes: [],
  edges: [],
};

export function getDefaultPages() {
  return [schemaPage, emptyPage];
}

export function getDefaultFileName() {
  return 'Hightag Firestore ERD';
}
